import Api from '_services/Api/index';
import type { AxiosResponse } from 'axios';

const referralsRoute = 'referrals';
const referralLinkRoute = 'user/referral-link';

export type ReferralLinkResponse = {
    referral_uuid: string;
    sender_first_name: string | null;
};

export type ReferralOnboardPayload = {
    referral_uuid: string;
    anon_id: string;
};

export type ReferralOnboardResponse = {
    ok: boolean;
    first_touch_kept: boolean;
    referral_uuid: string | null;
};

export type ReferralClaimPayload = {
    anon_id: string;
};

export type ReferralClaimResponse = {
    ok: boolean;
    referral_uuid: string;
    receiver_user_id: number;
};

/**
 * Centralized API client for the referral attribution flow.
 *
 * Why this module lives here:
 * - Keeps referral endpoints in the same `_services/Api` layer used by the rest of the app,
 *   so requests consistently use shared auth/cookie handling, error handling, and request limiting.
 * - Provides one typed integration point for both guest and authenticated referral steps,
 *   which avoids scattering endpoint strings and payload contracts across UI code.
 *
 * Overall use:
 * - `show` resolves a public referral UUID for pre-signup context.
 * - `onboard` records first-touch attribution for an anonymous user.
 * - `claim` binds that anonymous attribution to the signed-in user.
 * - `myLink` returns the current user's shareable referral link metadata.
 */
const ReferralApi = {
    /**
     * Get or create the current user's referral link.
     */
    myLink: (): Promise<AxiosResponse<ReferralLinkResponse>> =>
        Api.get(referralLinkRoute),

    /**
     * Resolve public referral metadata from referral UUID.
     */
    show: (
        referralUuid: string,
    ): Promise<AxiosResponse<ReferralLinkResponse>> =>
        Api.get(`${referralsRoute}/${encodeURIComponent(referralUuid)}`),

    /**
     * Register first-touch referral onboarding for an anonymous user.
     */
    onboard: (
        data: ReferralOnboardPayload,
    ): Promise<AxiosResponse<ReferralOnboardResponse>> =>
        Api.post(`${referralsRoute}/onboard`, data),

    /**
     * Claim an onboarded referral for the authenticated user.
     */
    claim: (
        data: ReferralClaimPayload,
    ): Promise<AxiosResponse<ReferralClaimResponse>> =>
        Api.post(`${referralsRoute}/claim`, data),
};

export default ReferralApi;
